// Rewards Program service. Reads + writes against `credits.client_credit_ledger`,
// `referrals.program_policies`, `referrals.rate_settings`, `referrals.commissions`.
//
// Module: rewards (UI module ID; covers `credits.*` + `referrals.*`).

import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import { getClientDisplayNamesByIds } from './clients';

/** See products.ts for why this shim exists. */
const sbUntyped = (): any => supabase() as unknown as any; // eslint-disable-line @typescript-eslint/no-explicit-any

// ---------- types --------------------------------------------------------

export type CreditTransactionType =
  | 'referral_credit'
  | 'manual_credit'
  | 'manual_debit'
  | 'order_payment'
  | 'refund'
  | 'correction';

export interface ClientCreditLedgerRow {
  id: string;
  org_id: string;
  client_id: string;
  transaction_type: CreditTransactionType;
  amount_cents: number;
  balance_before_cents: number;
  balance_after_cents: number;
  related_sale_id: string | null;
  related_payment_id: string | null;
  related_commission_id: string | null;
  corrects_transaction_id: string | null;
  notes: string | null;
  processed_by: string | null;
  created_at: string;
}

export type CommissionType = 'credit' | 'cash' | 'discount';

export type CommissionStatus =
  | 'pending'
  | 'approved'
  | 'paid'
  | 'cancelled'
  | 'reversed';

export type CommissionAction = 'approve' | 'deny' | 'pay';

export interface Commission {
  id: string;
  org_id: string;
  referrer_client_id: string;
  referred_client_id: string;
  sale_id: string | null;
  payment_id: string | null;
  commission_type: CommissionType;
  commission_rate: number;
  base_amount_cents: number;
  commission_amount_cents: number;
  status: CommissionStatus;
  notes: string | null;
  processed_by: string | null;
  processed_at: string | null;
  credit_ledger_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProgramPolicy {
  id: string;
  org_id: string;
  first_purchase_rate: number;
  subsequent_purchase_rate: number;
  effective_date: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface RateSetting {
  id: string;
  org_id: string;
  referrer_client_id: string | null;
  referred_client_id: string | null;
  first_purchase_rate: number | null;
  subsequent_purchase_rate: number | null;
  is_excluded: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommissionEnriched extends Commission {
  referrer_display_name: string;
  referred_display_name: string;
  sale_number: string | null;
}

// ---------- client credit ledger -----------------------------------------

export async function listCreditLedgerForClient(
  ctx: ActiveContext,
  clientId: string,
  opts: { limit?: number } = {},
): Promise<ClientCreditLedgerRow[]> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('credits')
    .from('client_credit_ledger')
    .select('*')
    .eq('org_id', orgId)
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 100);
  if (error) throw error;
  return (data ?? []) as unknown as ClientCreditLedgerRow[];
}

export async function getClientCreditBalance(
  ctx: ActiveContext,
  clientId: string,
): Promise<number> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('credits')
    .from('client_credit_ledger')
    .select('balance_after_cents')
    .eq('org_id', orgId)
    .eq('client_id', clientId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data?.balance_after_cents as number | undefined) ?? 0;
}

export async function applyManualCreditAdjustment(
  ctx: ActiveContext,
  input: {
    client_id: string;
    amount_cents: number; // positive = credit, negative = debit
    notes?: string | null;
  },
): Promise<ClientCreditLedgerRow> {
  const { orgId } = requireContext(ctx);
  // Use the underlying schema function shipped with credits.* migration.
  // fn_apply_transaction handles balance math and inserts an audited row.
  const { data, error } = await sbUntyped()
    .schema('credits')
    .rpc('fn_apply_transaction', {
      p_org_id: orgId,
      p_client_id: input.client_id,
      p_transaction_type:
        input.amount_cents >= 0 ? 'manual_credit' : 'manual_debit',
      p_amount_cents: Math.abs(input.amount_cents),
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as unknown as ClientCreditLedgerRow;
}

// ---------- referrals: commissions ---------------------------------------

export async function listCommissions(
  ctx: ActiveContext,
  opts: { status?: CommissionStatus; limit?: number } = {},
): Promise<Commission[]> {
  const { orgId } = requireContext(ctx);
  let q = sbUntyped()
    .schema('referrals')
    .from('commissions')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 100);
  if (opts.status) q = q.eq('status', opts.status);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Commission[];
}

export async function listCommissionsEnriched(
  ctx: ActiveContext,
  opts: { status?: CommissionStatus; limit?: number } = {},
): Promise<CommissionEnriched[]> {
  const rows = await listCommissions(ctx, opts);
  if (rows.length === 0) return [];

  const { orgId } = requireContext(ctx);
  const clientIds = rows.flatMap((r) => [r.referrer_client_id, r.referred_client_id]);
  const saleIds = [...new Set(rows.map((r) => r.sale_id).filter(Boolean))] as string[];

  const [namesById, saleNumbers] = await Promise.all([
    getClientDisplayNamesByIds(ctx, clientIds),
    fetchSaleNumbers(orgId, saleIds),
  ]);

  return rows.map((r) => ({
    ...r,
    referrer_display_name:
      namesById.get(r.referrer_client_id) ?? r.referrer_client_id.slice(0, 8),
    referred_display_name:
      namesById.get(r.referred_client_id) ?? r.referred_client_id.slice(0, 8),
    sale_number: r.sale_id ? saleNumbers.get(r.sale_id) ?? null : null,
  }));
}

async function fetchSaleNumbers(
  orgId: string,
  saleIds: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (saleIds.length === 0) return out;

  const { data, error } = await sbUntyped()
    .schema('orders')
    .from('orders')
    .select('id, number')
    .eq('org_id', orgId)
    .in('id', saleIds);
  if (error) throw error;

  for (const row of (data ?? []) as Array<{ id: string; number: string }>) {
    out.set(row.id, row.number);
  }
  return out;
}

export async function updateCommissionStatus(
  ctx: ActiveContext,
  input: {
    commission_id: string;
    action: CommissionAction;
    notes?: string | null;
  },
): Promise<Commission> {
  requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('referrals')
    .rpc('rpc_update_commission_status', {
      p_commission_id: input.commission_id,
      p_action: input.action,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as unknown as Commission;
}

export async function listCommissionsForClient(
  ctx: ActiveContext,
  clientId: string,
  role: 'referrer' | 'referred' = 'referrer',
): Promise<Commission[]> {
  const { orgId } = requireContext(ctx);
  const col = role === 'referrer' ? 'referrer_client_id' : 'referred_client_id';
  const { data, error } = await sbUntyped()
    .schema('referrals')
    .from('commissions')
    .select('*')
    .eq('org_id', orgId)
    .eq(col, clientId)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as Commission[];
}

// ---------- referrals: policy + rate-settings -----------------------------

export async function getCurrentProgramPolicy(
  ctx: ActiveContext,
): Promise<ProgramPolicy | null> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('referrals')
    .from('program_policies')
    .select('*')
    .eq('org_id', orgId)
    .order('effective_date', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as ProgramPolicy | null;
}

export async function upsertProgramPolicy(
  ctx: ActiveContext,
  input: {
    first_purchase_rate: number;
    subsequent_purchase_rate: number;
    effective_date: string;
    notes?: string | null;
  },
): Promise<ProgramPolicy> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('referrals')
    .from('program_policies')
    .insert({ ...input, org_id: orgId } as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ProgramPolicy;
}

export async function listRateSettings(ctx: ActiveContext): Promise<RateSetting[]> {
  const { orgId } = requireContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('referrals')
    .from('rate_settings')
    .select('*')
    .eq('org_id', orgId)
    .order('updated_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as RateSetting[];
}

// ---------- summary ------------------------------------------------------

export interface RewardsSummary {
  pending_commission_count: number;
  pending_commission_cents: number;
  total_credit_outstanding_cents: number;
}

export async function getRewardsSummary(ctx: ActiveContext): Promise<RewardsSummary> {
  const { orgId } = requireContext(ctx);
  const { data: pending, error: pendErr } = await sbUntyped()
    .schema('referrals')
    .from('commissions')
    .select('commission_amount_cents')
    .eq('org_id', orgId)
    .eq('status', 'pending');
  if (pendErr) throw pendErr;
  const pendRows = (pending ?? []) as Array<{ commission_amount_cents: number }>;
  const pendingTotal = pendRows.reduce((a, r) => a + Number(r.commission_amount_cents), 0);

  // Outstanding credit: sum of latest balance_after_cents per client.
  // We avoid a giant scan via a small RPC; if absent fall back to 0.
  let outstanding = 0;
  try {
    const { data, error } = await sbUntyped()
      .schema('credits')
      .rpc('rpc_org_credit_outstanding_cents', { p_org_id: orgId });
    if (!error && data !== null && data !== undefined) {
      outstanding = Number(data) || 0;
    }
  } catch {
    /* RPC not present in older deploys; default to 0 */
  }

  return {
    pending_commission_count: pendRows.length,
    pending_commission_cents: pendingTotal,
    total_credit_outstanding_cents: outstanding,
  };
}
